/**
 * SoundBank — Web Audio variation pool + composable sound design.
 *
 * Three layers of abstraction:
 *
 *   1. **Group** — one logical sound (e.g. "pop-land") backed by N audio
 *      files (variations). Configured with per-group defaults: base
 *      volume, pitch range, pan range. Every `play(group)` picks a
 *      random variation, applies the defaults, and applies the caller's
 *      per-play modulation on top.
 *
 *   2. **Mix** — N groups fired together with per-layer delays, pitches
 *      and volumes. `playMix([layers])` is the raw form; one-off layered
 *      cues build their MixLayer[] inline at the call site.
 *
 *   3. **Cue** — a named mix registered once with `defineCue(name, layers)`
 *      and fired anywhere via `playCue(name, opts?)`. Cues are the unit
 *      of reusable sound design — every game event maps to ONE named
 *      cue, and you tune that cue's composition in a single place.
 *
 * Auto-normalization: every loaded clip is scanned for its peak
 * amplitude on load and given a per-variation gain factor so all
 * variations come out the speakers at the same loudness regardless of
 * how hot or quiet the source file was.
 *
 * AudioBuffer-based (not HTMLAudioElement) so we get sample-accurate
 * timing, true playbackRate pitch shift, and proper mixing through a
 * master gain bus. Each `play` allocates a fresh BufferSource (Web
 * Audio one-shot convention); BufferSource auto-disposes after stop().
 */
export type LoadResult = { loaded: number; failed: number; group: string };

export interface PlayOpts {
  /** Playback rate multiplier — 1.0 = original, 2.0 = +1 octave, 0.5 = -1 octave. */
  pitch?: number;
  /** Linear volume — 1.0 = group default, 0..N. Multiplied with the
   *  group's default volume + the variation's auto-normalization gain. */
  volume?: number;
  /** Delay before playback starts, in milliseconds. Use for sequenced layers. */
  delayMs?: number;
  /** Force a specific variation index (mod pool size) instead of random. */
  variation?: number;
  /** Stereo pan, -1 = full left, 0 = center, 1 = full right. Adds onto
   *  the group's panJitter if any. */
  pan?: number;
  /** Optional world-space emit position. If provided, the play is
   *  routed through a spatial chain: distance attenuation, listener-
   *  relative stereo pan, and a low-pass filter that cuts highs as
   *  distance grows ("muffled when far"). Listener position + zoom is
   *  set externally via setListener(). When both `pan` and spatial pan
   *  are present they ADD. */
  position?: { x: number; z: number };
}

export interface MixLayer extends PlayOpts {
  group: string;
}

/** Per-group config — file list, subfolder override, baseline modulation.
 *  The subfolder defaults to the group's own name so each logical group
 *  has its own folder on disk (`public/sounds/<group>/<file>`). */
export interface GroupConfig {
  /** File basenames (extension optional). Subfolder is prepended
   *  automatically via the `dir` field (or the group name). */
  files: string[];
  /** Subfolder under `/sounds/` to load from. Defaults to the group
   *  name itself — e.g. group "pop-land" loads from /sounds/pop-land/.
   *  Set explicitly if you want multiple groups to share a folder
   *  (rare) or to load files from the root (legacy). */
  dir?: string;
  defaults?: GroupDefaults;
}

/** Per-group defaults baked into a registered group. Every play of
 *  this group inherits these before per-call modulation is applied. */
export interface GroupDefaults {
  /** Base volume multiplier — applied to every play. Use this to bring
   *  a noisy sample's level down, or push a quiet one up. Default 1.0. */
  volume?: number;
  /** Base pitch multiplier — applied to every play. Default 1.0. */
  pitch?: number;
  /** Random pitch variation as a +/- fraction (e.g. 0.05 = ±5%).
   *  Multiplied on top of the caller's pitch so no two consecutive
   *  plays sound identical. Default 0. */
  pitchJitter?: number;
  /** Random pan variation as a +/- fraction. e.g. 0.3 puts plays
   *  anywhere from L0.3 to R0.3 randomly. Default 0. */
  panJitter?: number;
  /** Force playback to stop after N milliseconds, with a short linear
   *  fadeout (~15ms) to avoid the click that a hard cut would produce.
   *  Use this when some variations in a group are longer than the
   *  action's natural rhythm (e.g. hoe taps should be ≤150ms even if
   *  the source clip is 400ms). Unset = play the full clip. */
  maxLengthMs?: number;
}

/** Optional cue-level modulation applied uniformly across every layer. */
export interface CueOpts {
  /** Multiplier applied to every layer's pitch. */
  pitchMul?: number;
  /** Multiplier applied to every layer's volume. */
  volumeMul?: number;
  /** Added to every layer's delayMs — shifts the whole cue later. */
  delayMs?: number;
  /** Per-cue stereo pan offset added to every layer's pan. */
  pan?: number;
  /** World-space emit position applied to EVERY layer of the cue.
   *  e.g. `playCue('chop-wood', { position: tileWorldPos(c, r) })` so
   *  the impact + crack layers both emit from the tree's location.
   *  Distance/pan/lowpass are computed per-play in the bank. */
  position?: { x: number; z: number };
}

/** Loudness target for auto-normalization — every loaded variation gets
 *  a per-buffer gain factor so its peak matches this value. 0.8 leaves
 *  headroom for the burst-climb pitch shift + per-call volume overrides
 *  without clipping. */
const TARGET_PEAK = 0.8;

/** Audio file extensions decodeAudioData accepts. If a filename in a
 *  group has any of these as its suffix we use it as-is; otherwise we
 *  append ".mp3" for back-compat with the original Freesound flow. */
const AUDIO_EXTS = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.opus'];

interface PoolEntry {
  buf: AudioBuffer;
  /** Per-clip normalization gain — multiplier needed to bring this
   *  clip's peak up to TARGET_PEAK. */
  gain: number;
}

interface PoolGroup {
  entries: PoolEntry[];
  defaults: GroupDefaults;
  fileList: string[];
}

export class SoundBank {
  private ctx: AudioContext;
  /** Registered variation groups keyed by logical name. */
  private pools = new Map<string, PoolGroup>();
  /** Named composite cues — each is a list of MixLayers fired together. */
  private cues = new Map<string, MixLayer[]>();
  /** Master gain bus — every sample routes through this so we can mute /
   *  duck the SFX layer globally if we ever add a music bus. */
  private master: GainNode;
  /** Listener world position + zoom, set externally each frame.
   *  Drives the spatial chain for positional plays. */
  private listener = { x: 0, z: 0, zoom: 1 };

  constructor(ctx: AudioContext) {
    this.ctx = ctx;
    this.master = ctx.createGain();
    // Master SFX bus runs at ~half level by default — most call sites
    // produce a peak around 0.8 (TARGET_PEAK), so 0.5 master keeps the
    // mix comfortably in the -10dB range. Cuable via setMasterVolume.
    this.master.gain.value = 0.5;
    this.master.connect(ctx.destination);
  }

  // ────────────────────────────────────────────────────────────────────
  // Spatial / listener
  // ────────────────────────────────────────────────────────────────────

  /** Set the current listener position + zoom factor. Call per frame
   *  from the rAF loop with the camera target's world coords and the
   *  camera zoom (1.0 = overview, larger = zoomed in). Higher zoom
   *  shrinks the perceived distance so sounds feel "right there"
   *  during close-ups and "off in the distance" during overview pan. */
  setListener(x: number, z: number, zoom: number): void {
    this.listener.x = x;
    this.listener.z = z;
    this.listener.zoom = Math.max(0.01, zoom);
  }

  /** Read-only view of the current listener state. */
  getListener(): { x: number; z: number; zoom: number } {
    return { ...this.listener };
  }

  /** Compute the spatial factors for an emit position relative to the
   *  current listener. Returns:
   *    - gain ∈ [0,1] — distance attenuation. 1 within NEAR, 0 past FAR.
   *    - pan ∈ [-1,1] — stereo offset from x-axis (camera-right).
   *    - lowpassHz — air-absorption cutoff. Falls from 22kHz (close) to
   *                 ~700Hz (far edge of audibility).
   *
   *  The zoom factor scales the perceived distance — at 2.6× zoom a
   *  10-unit world offset feels like ~3.8 units of audio distance,
   *  matching the visual sense that things are right in front of you.
   *
   *  Used by play() at fire-time for one-shots and by PositionalEmitter
   *  each frame for continuous moving sources. Pure function — no side
   *  effects, safe to call freely. */
  spatialFactors(pos: { x: number; z: number }):
    { gain: number; pan: number; lowpassHz: number } {
    const dx = pos.x - this.listener.x;
    const dz = pos.z - this.listener.z;
    const rawDist = Math.sqrt(dx * dx + dz * dz);
    const dist = rawDist / this.listener.zoom;
    // Rolloff curve — full vol within NEAR units, silent past FAR.
    // Gamma curve so the audible drop reads as "halving twice" rather
    // than a perceptual cliff.
    const NEAR = 1.5;
    const FAR = 26;
    let gain: number;
    if (dist <= NEAR) gain = 1;
    else if (dist >= FAR) gain = 0;
    else gain = Math.pow(1 - (dist - NEAR) / (FAR - NEAR), 1.7);
    // Air absorption — high frequencies attenuate more with distance.
    // gamma 0.6 keeps near-field bright while pulling far sources
    // strongly into the lows ("muffled-when-far" effect).
    let cutoffT: number;
    if (dist <= NEAR) cutoffT = 1;
    else if (dist >= FAR) cutoffT = 0;
    else cutoffT = 1 - (dist - NEAR) / (FAR - NEAR);
    const lowpassHz = 700 + (22000 - 700) * Math.pow(cutoffT, 0.6);
    // Stereo pan — x-axis offset from listener, scaled by zoom so
    // close-up plots feel wide-stereo and overview plays mostly centred.
    const panRange = 12 / Math.max(0.5, this.listener.zoom);
    const pan = clamp(dx / panRange, -1, 1);
    return { gain, pan, lowpassHz };
  }

  // ────────────────────────────────────────────────────────────────────
  // Loading
  // ────────────────────────────────────────────────────────────────────

  /** Decode N files in parallel and register them as variations of one
   *  logical group. Files are loaded from `/sounds/<dir>/<file>` where
   *  `dir` defaults to the group name. Filenames can include an audio
   *  extension (.wav, .ogg, .mp3, .m4a, .aac, .flac, .opus) — if
   *  omitted, ".mp3" is assumed. Per-group defaults bake in baseline
   *  volume / pitch / jitter so call sites don't have to repeat the
   *  modulation math. */
  async load(group: string, files: string[], defaults: GroupDefaults = {}, dir?: string): Promise<LoadResult> {
    const folder = dir ?? group;
    const entries: PoolEntry[] = [];
    let failed = 0;
    await Promise.all(files.map(async (name) => {
      try {
        const url = resolveAudioUrl(`${folder}/${name}`);
        const res = await fetch(url);
        if (!res.ok) { failed++; return; }
        const arr = await res.arrayBuffer();
        const buf = await this.ctx.decodeAudioData(arr);
        const peak = this.peakAmplitude(buf);
        // Normalize each clip to TARGET_PEAK. Guard against
        // pathological silent files (peak ~= 0) which would otherwise
        // produce an infinite gain.
        const gain = peak > 1e-4 ? (TARGET_PEAK / peak) : 1;
        entries.push({ buf, gain });
      } catch {
        failed++;
      }
    }));
    if (entries.length > 0) {
      this.pools.set(group, { entries, defaults, fileList: files });
    }
    return { group, loaded: entries.length, failed };
  }

  /** Convenience wrapper — load from a single GroupConfig object. */
  loadGroup(group: string, cfg: GroupConfig): Promise<LoadResult> {
    return this.load(group, cfg.files, cfg.defaults ?? {}, cfg.dir);
  }

  /** Walk every sample in every channel and return the absolute peak.
   *  Used to compute the per-clip normalization gain. Cheap — a
   *  half-second 48kHz mono clip is ~24k samples, scanned in <1ms. */
  private peakAmplitude(buf: AudioBuffer): number {
    let peak = 0;
    for (let ch = 0; ch < buf.numberOfChannels; ch++) {
      const data = buf.getChannelData(ch);
      for (let i = 0; i < data.length; i++) {
        const v = data[i] < 0 ? -data[i] : data[i];
        if (v > peak) peak = v;
      }
    }
    return peak;
  }

  // ────────────────────────────────────────────────────────────────────
  // Introspection
  // ────────────────────────────────────────────────────────────────────

  has(group: string): boolean {
    const p = this.pools.get(group);
    return !!p && p.entries.length > 0;
  }

  variationCount(group: string): number {
    return this.pools.get(group)?.entries.length ?? 0;
  }

  /** Pick a random variation from a group and return its
   *  AudioBuffer + normalization gain. Used by the AmbientMixer to
   *  spin up its own looping source nodes — one-shot `play()` isn't
   *  suitable for sustained ambient beds. Returns null if the group
   *  isn't loaded. */
  randomBuffer(group: string): { buf: AudioBuffer; gain: number } | null {
    const pool = this.pools.get(group);
    if (!pool || pool.entries.length === 0) return null;
    const idx = Math.floor(Math.random() * pool.entries.length);
    return pool.entries[idx];
  }

  /** Get the AudioContext so external systems (AmbientMixer) can
   *  build their own source nodes that share the same audio graph. */
  audioContext(): AudioContext {
    return this.ctx;
  }

  /** Get the master gain bus — external systems can connect their
   *  own gain chains here so they obey the global SFX volume. */
  masterBus(): GainNode {
    return this.master;
  }

  hasCue(name: string): boolean {
    return this.cues.has(name);
  }

  // ────────────────────────────────────────────────────────────────────
  // Playback
  // ────────────────────────────────────────────────────────────────────

  /** Play one variation of a group. Applies the group's defaults +
   *  random jitter, then the caller's per-play modulation on top.
   *  Returns false if the group isn't loaded (caller can run a synth
   *  fallback).
   *
   *  When `opts.position` is set the play is routed through a spatial
   *  chain: distance attenuation gain, listener-relative stereo pan,
   *  and an air-absorption lowpass. Plays whose computed gain falls
   *  below audibility (~−60dB) are silently dropped (return true so
   *  the caller doesn't run a fallback). */
  play(group: string, opts: PlayOpts = {}): boolean {
    const pool = this.pools.get(group);
    if (!pool || pool.entries.length === 0) return false;
    const idx = opts.variation != null
      ? ((opts.variation % pool.entries.length) + pool.entries.length) % pool.entries.length
      : Math.floor(Math.random() * pool.entries.length);
    const entry = pool.entries[idx];
    const d = pool.defaults;

    // Spatial factors — computed up-front so we can early-out for
    // sources beyond the audible range.
    let spatial: { gain: number; pan: number; lowpassHz: number } | null = null;
    if (opts.position) {
      spatial = this.spatialFactors(opts.position);
      if (spatial.gain < 1e-3) return true;   // beyond hearing — skip
    }

    // Effective pitch = caller × group base × random jitter.
    const jitterPitch = d.pitchJitter
      ? 1 + (Math.random() * 2 - 1) * d.pitchJitter
      : 1;
    const finalPitch = clamp(
      (opts.pitch ?? 1) * (d.pitch ?? 1) * jitterPitch,
      0.25, 4,
    );

    // Effective volume = caller × group base × per-clip normalization
    // × spatial distance attenuation (if positional).
    const finalVolume = Math.max(0,
      (opts.volume ?? 1) * (d.volume ?? 1) * entry.gain
        * (spatial ? spatial.gain : 1),
    );

    // Effective pan = caller offset + group's random pan jitter +
    // spatial pan (if positional). Spatial pan dominates pan jitter for
    // world-positioned plays since the listener-relative direction is
    // the truth.
    const jitterPan = d.panJitter
      ? (Math.random() * 2 - 1) * d.panJitter
      : 0;
    const finalPan = clamp(
      (opts.pan ?? 0) + jitterPan + (spatial ? spatial.pan : 0),
      -1, 1,
    );

    // ── Build the audio graph. Chain:
    //    src → gain → [lowpass if spatial] → [panner if non-zero] → master
    const src = this.ctx.createBufferSource();
    src.buffer = entry.buf;
    src.playbackRate.value = finalPitch;
    const gain = this.ctx.createGain();
    gain.gain.value = finalVolume;
    src.connect(gain);
    let tail: AudioNode = gain;
    if (spatial) {
      const lpf = this.ctx.createBiquadFilter();
      lpf.type = 'lowpass';
      lpf.frequency.value = spatial.lowpassHz;
      lpf.Q.value = 0.7;
      tail.connect(lpf);
      tail = lpf;
    }
    if (finalPan !== 0 && this.ctx.createStereoPanner) {
      const panNode = this.ctx.createStereoPanner();
      panNode.pan.value = finalPan;
      tail.connect(panNode);
      tail = panNode;
    }
    tail.connect(this.master);

    const when = this.ctx.currentTime + Math.max(0, (opts.delayMs ?? 0) / 1000);
    src.start(when);
    // If the group has a maxLengthMs cap, schedule a short fadeout
    // followed by a stop — prevents the click that a hard cut would
    // produce mid-waveform.
    if (d.maxLengthMs != null && d.maxLengthMs > 0) {
      const FADE = 0.015;   // 15ms linear fade
      const stopAt = when + (d.maxLengthMs / 1000);
      const fadeStart = Math.max(when, stopAt - FADE);
      gain.gain.setValueAtTime(finalVolume, fadeStart);
      gain.gain.linearRampToValueAtTime(0.0001, stopAt);
      src.stop(stopAt + 0.005);
    }
    return true;
  }

  /** Play multiple groups together as a single "mix" — layered or
   *  sequenced via per-layer delayMs. Returns the count of layers that
   *  actually fired (had loaded groups). */
  playMix(layers: MixLayer[]): number {
    let fired = 0;
    for (const layer of layers) {
      if (this.play(layer.group, layer)) fired++;
    }
    return fired;
  }

  /** Register a named composite cue. The cue is just a stored MixLayer[]
   *  — every game event in the codebase maps to one of these by name, so
   *  the sound design lives in ONE place and call sites stay clean. */
  defineCue(name: string, layers: MixLayer[]): void {
    this.cues.set(name, layers);
  }

  /** Fire a registered cue. `opts` is uniformly applied across every
   *  layer of the cue — pitchMul/volumeMul scale the whole composition,
   *  delayMs shifts it later, pan offsets every layer's pan, and
   *  position routes every layer through the spatial chain (so a
   *  multi-layer "chop-wood" cue emits the impact + crack + tail all
   *  from the same tree's world coordinate). Returns the count of
   *  layers that actually fired. */
  playCue(name: string, opts: CueOpts = {}): number {
    const def = this.cues.get(name);
    if (!def) return 0;
    const pitchMul = opts.pitchMul ?? 1;
    const volumeMul = opts.volumeMul ?? 1;
    const dMs = opts.delayMs ?? 0;
    const panAdd = opts.pan ?? 0;
    const pos = opts.position;
    // Materialize a transient MixLayer[] with the cue overrides folded
    // into each layer's PlayOpts. We don't mutate the stored definition.
    const layers: MixLayer[] = def.map(l => ({
      group: l.group,
      variation: l.variation,
      pitch: (l.pitch ?? 1) * pitchMul,
      volume: (l.volume ?? 1) * volumeMul,
      delayMs: (l.delayMs ?? 0) + dMs,
      pan: (l.pan ?? 0) + panAdd,
      position: pos ?? l.position,
    }));
    return this.playMix(layers);
  }

  /** Set master SFX volume (0..N). */
  setMasterVolume(v: number) {
    this.master.gain.value = Math.max(0, v);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Build the fetch URL for a sound file. Accepts:
 *    - "name"            → /sounds/name.mp3   (back-compat default)
 *    - "name.wav"        → /sounds/name.wav
 *    - "subdir/name.ogg" → /sounds/subdir/name.ogg
 *    - "stone 2.mp3"     → /sounds/stone%202.mp3  (spaces URL-encoded)
 *    - "name_#1.wav"     → /sounds/name_%231.wav  (# encoded — would be a URL fragment otherwise)
 *    - "name_—_.wav"     → /sounds/name_%E2%80%94_.wav  (em-dash encoded)
 *
 *  Per-segment encodeURIComponent so slashes survive but every other
 *  special character (spaces, #, em-dashes, ?, &) gets properly
 *  percent-encoded. encodeURI alone would leave # untouched, which is
 *  the URL-fragment delimiter and would break fetches like
 *  "file_#1.wav".
 */
function resolveAudioUrl(name: string): string {
  const lower = name.toLowerCase();
  const hasExt = AUDIO_EXTS.some(ext => lower.endsWith(ext));
  const full = `${name}${hasExt ? '' : '.mp3'}`;
  const encoded = full.split('/').map(encodeURIComponent).join('/');
  return `/sounds/${encoded}`;
}
